import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import {
  ArrowRight, Sparkles, ShieldCheck, Truck, Palette, ChevronLeft, ChevronRight,
  Camera, Star, Award, Wand2, MessageSquare, ArrowUpRight, Layers, Hash, Type, Gift, ScanFace,
} from "lucide-react";
import { HOME } from "@/constants/testIds";
import { useSiteSettings, useSeoTags } from "@/components/SiteSettings";
import PromoBannerSlot from "@/components/PromoBannerSlot";
import CrossfadeImage from "@/components/CrossfadeImage";
import { buildOrganizationSchema, buildItemListSchema, SITE_ORIGIN } from "@/hooks/useSEO";
import { cacheKey, readCache, writeCache } from "@/lib/pageCache";
import { sanitizeRichText } from "@/lib/sanitize";
import { proxify, proxifySrcSet } from "@/lib/imgProxy";
import { useGender } from "@/context/GenderContext";
import { useImpressionTracker, trackClick } from "@/hooks/useAnalytics";

const ICONS = { ShieldCheck, Truck, Palette, Sparkles, Award, Wand2, MessageSquare, ArrowUpRight, Layers, Hash, Star, Type };

// Local cache keys — return visitors get instant render from localStorage while
// the network refresh happens in the background. This fixes the "empty/blank
// site for 1-2 seconds before sections load" bug on production.
const HOME_KEYS = {
  sections: cacheKey("home_sections"),
  featured: cacheKey("home_featured"),
  cats: cacheKey("home_cats"),
  reviews: cacheKey("home_reviews"),
};

/**
 * useDragGuard — prevents `<Link>` clicks from firing while the user is
 * actively swiping a horizontal scroller. Listens on touchstart/touchmove
 * (and mousedown/mousemove for desktop drag-scroll) and, if the pointer
 * moved more than DRAG_THRESHOLD px, swallows the next click in capture
 * phase. Reset happens automatically on next touchstart.
 *
 * Usage:
 *   const drag = useDragGuard();
 *   <div {...drag} className="overflow-x-auto …">…</div>
 */
const DRAG_THRESHOLD = 8;
function useDragGuard() {
  const startX = useRef(0);
  const startY = useRef(0);
  const dragged = useRef(false);
  const onTouchStart = (e) => {
    if (!e.touches?.length) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    dragged.current = false;
  };
  const onTouchMove = (e) => {
    if (!e.touches?.length) return;
    const dx = Math.abs(e.touches[0].clientX - startX.current);
    const dy = Math.abs(e.touches[0].clientY - startY.current);
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) dragged.current = true;
  };
  const onPointerDown = (e) => {
    startX.current = e.clientX;
    startY.current = e.clientY;
    dragged.current = false;
  };
  const onPointerMove = (e) => {
    if (e.buttons === 0) return; // not dragging
    const dx = Math.abs(e.clientX - startX.current);
    const dy = Math.abs(e.clientY - startY.current);
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) dragged.current = true;
  };
  const onClickCapture = (e) => {
    if (dragged.current) {
      e.preventDefault();
      e.stopPropagation();
      dragged.current = false;
    }
  };
  return { onTouchStart, onTouchMove, onPointerDown, onPointerMove, onClickCapture };
}

export default function Home() {
  // Hydrate from localStorage on first paint — instant rendering for repeat visitors.
  const [sections, setSections] = useState(() => readCache(HOME_KEYS.sections, []));
  const [data, setData] = useState(() => readCache(HOME_KEYS.featured, { featured: [], best_sellers: [], customizable: [] }));
  const [cats, setCats] = useState(() => readCache(HOME_KEYS.cats, []));
  const [featuredReviews, setFeaturedReviews] = useState(() => readCache(HOME_KEYS.reviews, []));
  const sectionsLoading = sections.length === 0;

  useEffect(() => {
    // sections drives the entire page structure — fetch it eagerly so the
    // home shell is built ASAP. This is the only LCP-critical call.
    // Skip if the early preload script in index.html already populated
    // localStorage AND state already has data — avoids a duplicate fetch
    // that the early script just made.
    api.get("/home-cms/sections").then((r) => {
      const v = r.data || [];
      setSections(v);
      writeCache(HOME_KEYS.sections, v);
    }).catch(() => { /* keep cached value */ });

    // Featured products, categories and reviews are BELOW the fold on first
    // paint. Stagger across separate idle frames so we don't punch a 250ms
    // burst into main-thread work (which inflated TBT to 290ms on mobile
    // PageSpeed). Each one gets its own idle slot so the scheduler can
    // interleave them with paint / layout work.
    const runIdle = (fn, delay = 0) => {
      const schedule = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          window.requestIdleCallback(fn, { timeout: 3500 });
        } else {
          setTimeout(fn, 1500);
        }
      };
      if (delay > 0) setTimeout(schedule, delay);
      else schedule();
    };
    runIdle(() => {
      api.get("/catalog/featured").then((r) => {
        setData(r.data);
        writeCache(HOME_KEYS.featured, r.data);
      }).catch(() => {});
    }, 0);
    runIdle(() => {
      api.get("/catalog/categories").then((r) => {
        setCats(r.data);
        writeCache(HOME_KEYS.cats, r.data);
      }).catch(() => {});
    }, 200);
    runIdle(() => {
      api.get("/reviews/featured").then((r) => {
        const v = r.data || [];
        setFeaturedReviews(v);
        writeCache(HOME_KEYS.reviews, v);
      }).catch(() => {});
    }, 500);
  }, []);

  const settings = useSiteSettings();
  // Compose JSON-LD: Organization (always) + WebSite + ItemList of featured products
  // (helps Google generate sitelinks search box & rich product snippets).
  const homeSchemas = useMemo(() => {
    const list = [
      buildOrganizationSchema(),
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: SITE_ORIGIN,
        name: "XYZPLUS",
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_ORIGIN}/shop?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ];
    const featured = data.featured || data.best_sellers || [];
    const itemList = buildItemListSchema(featured);
    if (itemList) list.push(itemList);
    return list;
  }, [data.featured, data.best_sellers]);

  useSeoTags({
    title: settings.home_seo_title || settings.seo_title,
    description: settings.home_seo_description || settings.seo_description,
    keywords: settings.home_seo_keywords || settings.seo_keywords,
    image: settings.home_og_image || settings.seo_og_image,
    canonical: SITE_ORIGIN + "/",
    schemas: homeSchemas,
  });

  return (
    <div data-testid="home-page">
      <PromoBannerSlot slot_id="home_hero_strip" variant="strip" />
      {/* AI Virtual Try-On promo — TOP mount. Only renders when admin sets
          home_banner_position="top" in /admin/tryon. Falls back to no-op
          otherwise so the layout is unchanged. */}
      {/* AI Virtual Try-On promo is now a first-class home section
          (type: "tryon_promo") — admin can drag-reorder it in /admin/home-editor.
          The hardcoded top/bottom mounts have been removed. */}
      {/* First-time visitor skeleton: shown only when we have nothing cached AND
          the network fetch is still pending. Prevents the "blank/preview-looking"
          flash on cold loads. Return visitors render directly from localStorage. */}
      {sectionsLoading && <HomeHeroSkeleton />}
      {sections.map((s, idx) => {
        // Insert WhyStrip after the hero (always shown, premium feature highlight)
        // Insert mid-1 promo banner right after Featured Categories
        const isAfterCategories = s.type === "featured_categories";
        const after = isAfterCategories ? (
          <PromoBannerSlot key={`${s.section_id}-banner`} slot_id="home_mid_1" />
        ) : null;
        let rendered = null;
        switch (s.type) {
          case "hero":              rendered = (s.config?.layout === "triple_strip") ? <HeroCampaignStrip key={s.section_id} cfg={s.config} /> : <HeroSection key={s.section_id} cfg={s.config} />; break;
          case "hot_deals":         rendered = <HotDealsBanner key={s.section_id} cfg={s.config} />; break;
          case "referral_promo":    rendered = <ReferralPromoBanner key={s.section_id} cfg={s.config} />; break;
          case "announce_bar":      rendered = <AnnounceBar key={s.section_id} cfg={s.config} />; break;
          case "partners_marquee":  rendered = <PartnersStrip key={s.section_id} cfg={s.config} />; break;
          case "trust_strip":       rendered = <TrustStrip key={s.section_id} cfg={s.config} />; break;
          case "featured_categories": rendered = <FeaturedCategories key={s.section_id} cfg={s.config} cats={cats} />; break;
          case "use_case_grid":     rendered = <UseCaseGrid key={s.section_id} cfg={s.config} />; break;
          case "featured_products": rendered = <FeaturedProducts key={s.section_id} cfg={s.config} products={data} reviews={featuredReviews} />; break;
          case "how_it_works":      rendered = <HowItWorks key={s.section_id} cfg={s.config} />; break;
          case "bulk_tiers":        rendered = <BulkTiersStrip key={s.section_id} cfg={s.config} />; break;
          case "studio_demo":       rendered = <StudioDemoLoop key={s.section_id} cfg={s.config} />; break;
          case "ai_design_promo":   rendered = <AIDesignPromoStrip key={s.section_id} cfg={s.config} />; break;
          case "testimonials":      rendered = <Testimonials key={s.section_id} cfg={s.config} />; break;
          case "cta_strip":         rendered = <CtaStrip key={s.section_id} cfg={s.config} />; break;
          case "tryon_promo":       rendered = <TryOnPromoBanner key={s.section_id} cfg={s.config} />; break;
          case "custom_html":       rendered = <CustomHtml key={s.section_id} cfg={s.config} />; break;
          default: rendered = null;
        }
        return <React.Fragment key={s.section_id || idx}>{rendered}{after}</React.Fragment>;
      })}
      {/* AI Virtual Try-On promo — BOTTOM mount. Renders when admin sets
          home_banner_position="bottom" (the default). */}
      <PromoBannerSlot slot_id="home_bottom_cta" />
    </div>
  );
}

// ============================================================================
// SECTION COMPONENTS — premium spacing, clean typography, less is more
// ============================================================================

function SectionShell({ children, className = "", testId, dark = false }) {
  return (
    <section
      data-testid={testId}
      className={`${dark ? "bg-zinc-50 text-[#111111] border-y border-zinc-200" : "bg-white"} py-6 md:py-10 ${className}`}
    >
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">{children}</div>
    </section>
  );
}

function Eyebrow({ children }) {
  return <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5722]">{children}</div>;
}

function H2({ children, className = "" }) {
  return (
    <h2 className={`font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#111111] leading-[1.05] ${className}`}>
      {children}
    </h2>
  );
}

// -------------------- HERO SKELETON (shown only on first cold load) --------------------
// Renders a layout-accurate placeholder while /home-cms/sections is in flight,
// so the page never shows an empty white middle (which previously looked like
// a brief "preview" of a different site to users).
function HomeHeroSkeleton() {
  return (
    <section className="relative bg-gradient-to-br from-rose-50 via-orange-50 to-pink-50 overflow-hidden" data-testid="home-hero-skeleton">
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-8 pb-12 md:pt-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Left — text placeholders */}
          <div className="lg:col-span-5 order-2 lg:order-1 space-y-4">
            <div className="h-3 w-32 bg-orange-200/70 rounded-full animate-pulse" />
            <div className="space-y-3">
              <div className="h-10 sm:h-12 lg:h-14 w-[85%] bg-zinc-200 rounded-full animate-pulse" />
              <div className="h-10 sm:h-12 lg:h-14 w-[65%] bg-zinc-200 rounded-full animate-pulse" />
            </div>
            <div className="h-4 w-[80%] bg-zinc-200/80 rounded-full animate-pulse" />
            <div className="grid grid-cols-2 gap-3 max-w-sm pt-3">
              <div className="h-12 bg-[#FF5722]/70 rounded-full animate-pulse" />
              <div className="h-12 bg-zinc-200 rounded-full animate-pulse" />
            </div>
          </div>
          {/* Right — collage placeholders */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="grid grid-cols-3 grid-rows-4 md:grid-cols-4 md:grid-rows-2 gap-2 sm:gap-3 h-[28rem] sm:h-[32rem] lg:h-[36rem]">
              <div className="col-span-3 row-span-4 md:col-span-2 md:row-span-2 bg-zinc-200 rounded-2xl animate-pulse" />
              <div className="hidden md:block md:col-span-1 md:row-span-2 bg-zinc-200 rounded-2xl animate-pulse" />
              <div className="hidden md:block md:col-span-1 md:row-span-1 bg-zinc-200 rounded-2xl animate-pulse" />
              <div className="hidden md:block md:col-span-1 md:row-span-1 bg-zinc-200 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// -------------------- HERO "SHOP FOR" (Bewakoof-style brand hero) --------------------
// Activated when cfg.layout === "shop_for". Layout mirrors Bewakoof's homepage
// pattern but uses XYZPLUS's brand orange instead of yellow. Sections:
//   1. Top social-proof strip
//   2. Large bright orange canvas with diagonal-grid pattern
//   3. Centered display "SHOP FOR" wordmark
//   4. 2-up (or 3-up if COUPLE slide present) full-bleed model-photo cards
//   5. Tagline under cards
// Slides schema:
//   { gender: "men"|"women"|"couple", image, label, cta_url, headline (optional) }
function HeroShopFor({ cfg }) {
  const slides = (cfg?.slides || []).filter((s) =>
    ["men", "women", "couple"].includes((s.gender || "").toLowerCase())
  );
  // Always show: first card per gender (men first, then women, then couple)
  const cards = ["men", "women", "couple"]
    .map((g) => slides.find((s) => (s.gender || "").toLowerCase() === g))
    .filter(Boolean);
  if (!cards.length) return null;
  const eyebrow = cfg?.eyebrow || "12,000+ Tees Printed · Trusted by 1,200+ Teams";
  const tagline = cfg?.tagline || "Homegrown Bengali Streetwear · Since 2024";

  return (
    <section className="relative bg-white" data-testid="hero-shop-for">
      {/* Top social proof strip — neutral cream like Bewakoof's */}
      <div className="bg-[#FFF5EC] text-[#111111] text-center py-2.5 text-xs sm:text-sm font-semibold">
        <span className="inline-flex items-center gap-2">
          <Sparkles size={14} className="text-[#FF5722]" />
          {eyebrow}
        </span>
      </div>

      {/* Main orange canvas */}
      <div className="relative overflow-hidden bg-[#FF5722]">
        {/* Diagonal grid pattern overlay — pure CSS, Bewakoof-style */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 60px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 60px)",
          }}
        />

        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-8 pb-12 md:pt-12 md:pb-20">
          {/* Centered "SHOP FOR" wordmark */}
          <div className="text-center text-white mb-8 md:mb-12">
            <div className="text-xs md:text-sm uppercase tracking-[0.32em] font-black opacity-90">
              XYZPLUS
            </div>
            <h1 className="mt-1 font-display font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.85]">
              SHOP FOR
            </h1>
          </div>

          {/* 2 (or 3) gender cards */}
          <div
            className={`grid gap-3 md:gap-5 ${
              cards.length === 3
                ? "grid-cols-1 sm:grid-cols-3"
                : "grid-cols-1 sm:grid-cols-2"
            }`}
          >
            {cards.map((card) => (
              <Link
                key={card.gender}
                to={card.cta_url || `/shop?category=${card.gender}-wear`}
                data-testid={`shop-for-card-${card.gender}`}
                className="group relative block bg-white rounded-2xl overflow-hidden aspect-[3/4] sm:aspect-[3/4.2]"
              >
                {card.image && (
                  <img
                    src={card.image}
                    alt={card.gender}
                    loading="eager"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {/* Bottom CTA pill — sits flush on the card like Bewakoof's */}
                <div className="absolute inset-x-4 bottom-4 md:inset-x-6 md:bottom-6">
                  <div className="bg-[#FFD500] text-black font-display font-black text-lg md:text-xl tracking-tight uppercase text-center py-3 md:py-3.5 rounded-lg group-hover:bg-[#111111] group-hover:text-white transition-colors">
                    {card.label || card.gender.toUpperCase()}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Tagline */}
          <div className="mt-8 md:mt-12 text-center text-white">
            <div className="font-display font-black text-2xl md:text-4xl tracking-tighter leading-tight">
              {tagline}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}




// -------------------- HERO CAMPAIGN STRIP (Bewakoof-style 3-up) --------------------
// Activated when cfg.layout === "triple_strip". Renders:
//   - Top pill toggle: MEN | WOMEN (yellow active)
//   - 3 simultaneous full-bleed vertical lifestyle cards (campaign banners)
//   - Auto-paginates through additional slides in batches of 3
//   - Each card has full-bleed photo + overlay headline + CTA
// Slides schema (per slide):
//   { gender: "men"|"women", image, headline, sub, cta_label, cta_url, eyebrow }
function HeroCampaignStrip({ cfg }) {
  const allSlides = cfg?.slides || [];
  const { gender } = useGender();
  const heroDrag = useDragGuard();
  const mobileScrollerRef = React.useRef(null);
  const [mobilePage, setMobilePage] = useState(0);

  const filtered = useMemo(
    () => allSlides.filter((s) => (s.gender || "men").toLowerCase() === gender),
    [allSlides, gender]
  );

  // Total pages = number of slides (1 card scroll per tick).
  const mobileTotalPages = Math.max(1, filtered.length);

  // Auto-scroll: every 5s advance by ONE card width so the strip slides
  // gently through every campaign tile. Reads the first child's offsetWidth so
  // changes to card sizing don't require updating the timer math.
  useEffect(() => {
    if (mobileTotalPages < 2) return;
    const el = mobileScrollerRef.current;
    if (!el) return;
    const t = setInterval(() => {
      setMobilePage((p) => {
        const next = (p + 1) % mobileTotalPages;
        const firstCard = el.firstElementChild;
        const step = firstCard ? firstCard.offsetWidth + 12 /* gap-3 */ : el.clientWidth;
        el.scrollTo({ left: next * step, behavior: "smooth" });
        return next;
      });
    }, 5000);
    return () => clearInterval(t);
  }, [mobileTotalPages, gender]);

  // Reset position when gender flips
  useEffect(() => { setMobilePage(0); }, [gender]);

  // Keep mobilePage in sync with actual scroll position (when user swipes manually)
  const onMobileScroll = () => {
    const el = mobileScrollerRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    const step = firstCard ? firstCard.offsetWidth + 12 : el.clientWidth;
    if (step > 0) {
      const idx = Math.round(el.scrollLeft / step);
      if (idx !== mobilePage) setMobilePage(idx);
    }
  };

  const mobileGoTo = (nextIdx) => {
    const el = mobileScrollerRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    const step = firstCard ? firstCard.offsetWidth + 12 : el.clientWidth;
    const target = ((nextIdx % mobileTotalPages) + mobileTotalPages) % mobileTotalPages;
    el.scrollTo({ left: target * step, behavior: "smooth" });
    setMobilePage(target);
  };

  return (
    <section ref={useImpressionTracker("hero")} className="relative bg-white" data-testid="hero-campaign-strip">
      {/* Single unified horizontal scroller — same smooth 1-card-at-a-time
          experience across mobile / tablet / desktop. Card widths scale per
          viewport so 1 → 2 → 3 cards become visible naturally. Arrows step
          one card; auto-scroll advances one card every 5s. */}
      <div className="max-w-[1600px] mx-auto pt-6 md:pt-8">
        <div className="relative">
          <div
            ref={mobileScrollerRef}
            onScroll={onMobileScroll}
            {...heroDrag}
            className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1 scroll-pl-4 scroll-pr-4 pl-4 md:pl-8"
            data-testid="hero-strip-scroller"
          >
            {filtered.map((slide, i) => (
              <div
                key={`h-${gender}-${i}`}
                className="snap-start flex-shrink-0 w-[85vw] sm:w-[55vw] md:w-[42vw] lg:w-[31vw] xl:w-[26vw] max-w-[520px]"
              >
                <CampaignCard slide={slide} />
              </div>
            ))}
            <div className="flex-shrink-0 w-1 md:w-4" aria-hidden="true" />
          </div>
          {mobileTotalPages > 1 && (
            <>
              <button
                onClick={() => mobileGoTo(mobilePage - 1)}
                aria-label="Previous campaigns"
                data-testid="hero-strip-prev"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-lg ring-1 ring-black/10 hover:bg-[#FF5722] hover:text-white text-[#111111] flex items-center justify-center active:scale-95 transition-all z-10"
              >
                <ChevronLeft size={20} strokeWidth={2.5} />
              </button>
              <button
                onClick={() => mobileGoTo(mobilePage + 1)}
                aria-label="Next campaigns"
                data-testid="hero-strip-next"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/95 backdrop-blur shadow-lg ring-1 ring-black/10 hover:bg-[#FF5722] hover:text-white text-[#111111] flex items-center justify-center active:scale-95 transition-all z-10"
              >
                <ChevronRight size={20} strokeWidth={2.5} />
              </button>
              {/* Pagination dots — one per slide */}
              <div className="mt-3 flex items-center justify-center gap-1.5">
                {Array.from({ length: mobileTotalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => mobileGoTo(i)}
                    aria-label={`Slide ${i + 1}`}
                    data-testid={`hero-strip-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === mobilePage ? "w-6 bg-[#FF5722]" : "w-1.5 bg-zinc-300 hover:bg-zinc-500"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// Single Bewakoof-style campaign tile — full-bleed vertical lifestyle photo
// with bold display typography overlay + corner badge + CTA pinned bottom-left.
function CampaignCard({ slide }) {
  if (!slide?.image) {
    return <div className="hidden md:block bg-zinc-100 rounded-2xl aspect-[3/4]" />;
  }
  // Bare-image mode: admin clears all text/CTA fields → render only the
  // clickable image. We read ONLY the new schema keys (eyebrow / headline)
  // so admins can confidently clear them via the editor without legacy
  // `badge` / `title` fields silently bleeding through.
  const headline = slide.headline || "";
  const eyebrow = slide.eyebrow || "";
  const sub = slide.sub || "";
  const ctaLabel = slide.cta_label || "";
  const href = slide.cta_url || "/shop";
  const hasAnyText = !!(headline || eyebrow || sub || ctaLabel);
  return (
    <Link
      to={href}
      onClick={() => trackClick("hero", href, headline || eyebrow)}
      data-testid={`campaign-card-${(headline || "card").toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
      className="group relative block overflow-hidden rounded-2xl bg-zinc-100 aspect-[3/4] md:aspect-[3/4.2]"
    >
      <img
        src={slide.image}
        alt={headline || "Campaign"}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Bottom gradient overlay — only when there IS text to read. Bare
          image cards stay crisp without a darkening overlay. */}
      {hasAnyText && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
      )}

      {/* Eyebrow / corner badge */}
      {eyebrow && (
        <div
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full"
          style={{
            backgroundColor: slide.badge_bg || "#FF5722",
            color: slide.badge_text_color || "#FFFFFF",
          }}
        >
          {eyebrow}
        </div>
      )}

      {/* Try-On pill — top-right corner, admin-toggled per slide.
          Optional `tryon_product_slug` sends the user to that specific PDP
          (where the "See how it looks on YOU" card lives above swatches).
          Empty slug → open the generic /try-on landing page. Uses
          stopPropagation so tapping the pill doesn't also fire the wrapping
          <Link>'s navigation. */}
      {slide.tryon_enabled && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const target = slide.tryon_product_slug ? `/p/${slide.tryon_product_slug}` : "/try-on";
            trackClick("hero", target, "Try On");
            window.location.assign(target);
          }}
          data-testid="hero-slide-tryon"
          className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-white/95 hover:bg-[#FF5722] hover:text-white text-[#FF5722] text-[11px] font-bold shadow-md backdrop-blur transition-colors"
        >
          <ScanFace size={13} /> Try On
          <span className="text-[8px] uppercase tracking-wider font-black bg-[#FF5722] text-white group-hover:bg-white group-hover:text-[#FF5722] px-1 py-px rounded">AI</span>
        </button>
      )}

      {/* Bottom content block — only render when any of headline / sub / cta exists.
          Each child is independently conditional so admin can mix-and-match. */}
      {(headline || sub || ctaLabel) && (
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
          {headline && (
            <h2 className="font-display font-black text-white tracking-tighter leading-[0.95] text-3xl md:text-4xl lg:text-5xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
              {headline}
            </h2>
          )}
          {sub && (
            <p className="mt-2 text-white/90 text-sm font-medium max-w-[90%] line-clamp-2">
              {sub}
            </p>
          )}
          {ctaLabel && (
            <div className="mt-4 inline-flex items-center gap-2 bg-white text-black text-xs font-black uppercase tracking-wider px-6 h-10 rounded-md group-hover:bg-[#FF5722] group-hover:text-white transition-colors">
              {ctaLabel}
              <ArrowRight size={14} />
            </div>
          )}
        </div>
      )}
    </Link>
  );
}



// -------------------- HERO (CustomInk-style: left text + right bento collage) --------------------
function HeroSection({ cfg }) {
  const slides = cfg?.slides || [];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touchStartX = useRef(null);
  const heroPreloadInitialized = useRef(false);
  const n = slides.length;

  useEffect(() => {
    if (paused || n < 2) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % n), 6000);
    return () => clearInterval(timer.current);
  }, [paused, n]);

  if (!n) return null;
  const active = slides[idx] || slides[0];

  // LCP boost: preload ONLY the active slide so the browser starts fetching it
  // BEFORE the React tree mounts the <img>. Subsequent slides are pre-decoded
  // lazily (during idle time) — previously we pre-decoded ALL slides on mount
  // which spiked initial bandwidth by 6-8MB on mobile and crushed LCP (was
  // 37.7s on PageSpeed mobile).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const rawHero = (slides[idx] || slides[0])?.image;
    if (!rawHero) return;
    // FIRST MOUNT (idx===0 + ref unset): bail out entirely. The
    // early-preload script in public/index.html has already created
    // link[data-hero-preload] with the correct href + imagesrcset BEFORE
    // React mounted. Re-assigning here causes a 2nd /api/img-proxy fetch
    // on desktop because of subtle URL-encoding differences between the
    // two code paths (iter79 finding). Subsequent slide rotations are
    // handled by the same effect — the ref flips to true after first run.
    if (!heroPreloadInitialized.current) {
      heroPreloadInitialized.current = true;
      // If the early script never ran (e.g., JS execution order quirk on
      // very old browsers, or the link was stripped), create the link now
      // as a safety net.
      if (!document.head.querySelector('link[data-hero-preload]')) {
        const isMobileInit = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
        const heroUrlInit = proxify(rawHero, isMobileInit ? 640 : 1600);
        const heroSrcSetInit = proxifySrcSet(rawHero);
        const linkInit = document.createElement("link");
        linkInit.rel = "preload";
        linkInit.as = "image";
        linkInit.setAttribute("data-hero-preload", "1");
        linkInit.setAttribute("fetchpriority", "high");
        linkInit.href = heroUrlInit;
        if (heroSrcSetInit) {
          linkInit.setAttribute("imagesrcset", heroSrcSetInit);
          linkInit.setAttribute("imagesizes", "(max-width: 768px) 100vw, 60vw");
        }
        document.head.appendChild(linkInit);
      }
      // schedule sibling pre-decode below; do NOT touch the existing link.
    } else {
      // Subsequent slide rotation: update the preload to point at the new slide.
      const isMobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
      const heroUrl = proxify(rawHero, isMobile ? 640 : 1600);
      const heroSrcSet = proxifySrcSet(rawHero);
      const link = document.head.querySelector('link[data-hero-preload]');
      if (link) {
        if (link.href !== heroUrl) link.href = heroUrl;
        if (heroSrcSet && link.getAttribute("imagesrcset") !== heroSrcSet) {
          link.setAttribute("imagesrcset", heroSrcSet);
          link.setAttribute("imagesizes", "(max-width: 768px) 100vw, 60vw");
        }
      }
    }
    // Defer pre-decoding of OTHER slides until the browser is idle so they
    // never compete with the LCP image for bandwidth/decoder. Stagger across
    // multiple idle frames to keep main-thread budget tight.
    if (n > 1 && typeof window !== "undefined") {
      const isMobileForSiblings = window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
      const idleQueue = (cb) => {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(cb, { timeout: 3000 });
        } else {
          setTimeout(cb, 1500);
        }
      };
      idleQueue(() => {
        for (let i = 1; i < n; i++) {
          const otherUrl = slides[(idx + i) % n]?.image;
          if (otherUrl) {
            const pre = new Image();
            pre.decoding = "async";
            pre.loading = "lazy";
            pre.src = proxify(otherUrl, isMobileForSiblings ? 640 : 1280);
          }
        }
      });
    }
    return () => { /* keep link across re-renders; cleaned by hero swap above */ };
  }, [idx, slides, n]);

  // Build bento collage: active slide is the LARGE tile (left of the right column),
  // remaining slides (or duplicates) fill the 3 small tiles. Limit to 4 visible tiles.
  const collage = [];
  for (let i = 0; i < 4; i++) {
    const s = slides[(idx + i) % n];
    if (s?.image) collage.push(s);
  }

  return (
    <section className="relative bg-white overflow-hidden" data-testid="hero-carousel">
      {/* Soft animated colour blobs in the background — premium polish */}
      <div className="hero-blob bg-[#FF5722]" style={{ top: "-120px", left: "-80px" }} />
      <div className="hero-blob bg-orange-300" style={{ bottom: "-160px", right: "-100px", animationDelay: "-9s" }} />
      <div className="absolute inset-0 bg-noise opacity-60 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-8 pb-12 md:pt-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* LEFT — compact copy column */}
          <div className="lg:col-span-5 order-2 lg:order-1" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            {/* Static brand tagline — sits ABOVE the rotating eyebrow so the
                XYZPLUS promise is the first thing customers read, regardless of
                which carousel slide is active. Kept lightweight (no animation)
                so it never adds CLS or layout shift between slides. */}
            <p
              className="font-display text-[13px] sm:text-sm font-semibold tracking-tight text-zinc-600 mb-3"
              data-testid="hero-brand-tagline"
            >
              More than apparel — <span className="text-[#FF5722] font-bold">it&apos;s a way to express who you are.</span>
            </p>
            <Eyebrow>{active.eyebrow || "Custom Apparel · Made in India"}</Eyebrow>
            {/* Title block — bounded height so CTAs never jump across slides */}
            <div className="mt-3 h-[7.5rem] sm:h-[9rem] lg:h-[10.5rem] overflow-hidden" key={`hero-text-${idx}`}>
              <h1
                className="hero-fade-text font-display font-extrabold text-[2.25rem] sm:text-5xl lg:text-[3.5rem] tracking-tight text-[#111111] leading-[1] line-clamp-2"
                data-testid={HOME.heroTitle}
              >
                {active.headline}
              </h1>
              <p className="hero-fade-text mt-3 text-sm sm:text-base text-zinc-600 leading-relaxed max-w-md line-clamp-2" style={{ animationDelay: "80ms" }}>
                {active.from_price
                  ? active.sub
                    ? active.sub.replace(/From\s*₹\s*\d+/i, `From ₹${active.from_price}`)
                    : `From ₹${active.from_price} · 5-7 day turnaround`
                  : active.sub}
              </p>
            </div>
            {/* CTAs — locked 2-column grid (no flex-wrap drift) */}
            <div className="mt-5 grid grid-cols-2 gap-3 max-w-sm" data-testid="hero-cta-row">
              <Link
                to={active.cta_url || "/custom"}
                data-testid={HOME.heroCtaPrimary}
                className="btn-industrial bg-[#FF5722] hover:bg-orange-600 text-white whitespace-nowrap"
              >
                {active.cta_label || "Start Designing"} <ArrowRight size={14} className="shrink-0" />
              </Link>
              <Link
                to="/catalog"
                className="btn-industrial border-2 border-zinc-300 text-[#111111] hover:bg-orange-600 hover:text-white whitespace-nowrap"
              >
                Browse Catalog
              </Link>
            </div>
            {/* Inline social proof */}
            <div className="mt-5 flex items-center gap-3 text-xs text-zinc-500">
              <div className="flex text-[#FF5722]">
                {[0,1,2,3,4].map((i) => <Star key={i} size={13} fill="#FF5722" strokeWidth={0} />)}
              </div>
              <span><b className="text-[#111111]">1,200+ teams</b> · ₹{active.from_price || 599} starting · 5-day delivery</span>
            </div>
            {/* Hero dots (only when >1 slide) */}
            {n > 1 && (
              <div className="mt-6 flex gap-1.5" data-testid="hero-dots">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Slide ${i + 1}`}
                    data-testid={`hero-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#FF5722]" : "w-3 bg-zinc-200 hover:bg-zinc-400"}`}
                  />
                ))}
              </div>
            )}

            {/* Trust & Utility dock — fills the bottom of the left column with high-density
                conversion signal so the column doesn't feel half-empty on desktop.
                Hidden on mobile where vertical space is precious. */}
            <div className="hidden md:block border-t border-zinc-200 pt-6 mt-8 w-full" data-testid="hero-trust-dock">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-700 mb-5"
                data-testid="hero-shipped-ticker"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF5722] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF5722]" />
                </span>
                <span><b className="text-[#111111]">Just shipped:</b> 50 Custom Hoodies → Bengaluru</span>
              </div>
              <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm text-zinc-700 font-medium" data-testid="hero-usp-grid">
                <div className="flex items-center gap-2"><span className="text-[#FF5722] font-bold">✓</span> 300-DPI artwork proofs</div>
                <div className="flex items-center gap-2"><span className="text-[#FF5722] font-bold">✓</span> 5-day average delivery</div>
                <div className="flex items-center gap-2"><span className="text-[#FF5722] font-bold">✓</span> Free design help</div>
                <div className="flex items-center gap-2"><span className="text-[#FF5722] font-bold">✓</span> Free shipping ₹999+</div>
              </div>
            </div>
          </div>

          {/* RIGHT — bento collage of 4 product mockups */}
          <div
            className="lg:col-span-7 order-1 lg:order-2"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].screenX; }}
            onTouchEnd={(e) => {
              if (touchStartX.current == null || n < 2) return;
              const dx = e.changedTouches[0].screenX - touchStartX.current;
              if (Math.abs(dx) > 50) {
                setIdx((cur) => (cur + (dx < 0 ? 1 : n - 1)) % n);
              }
              touchStartX.current = null;
            }}
          >
            <div className="grid grid-cols-3 grid-rows-4 md:grid-cols-4 md:grid-rows-2 gap-2 sm:gap-3 h-[28rem] sm:h-[32rem] lg:h-[36rem]">
              {/* Big tile — fills the WHOLE grid on mobile (Option A: single bold hero, swipe + dots for nav); 2×2 on desktop */}
              <Link
                to={active.cta_url || "/custom"}
                data-testid="hero-collage-0"
                className="relative col-span-3 row-span-4 md:col-span-2 md:row-span-2 overflow-hidden bg-zinc-100 group rounded-2xl"
              >
                {collage[0]?.image && (
                  <CrossfadeImage
                    src={collage[0].image}
                    alt={collage[0].alt || active.headline || "Hero collage"}
                    className="absolute inset-0 w-full h-full"
                    imgClassName="transition-transform duration-500 group-hover:scale-105"
                    kenBurns
                    eager
                    duration={800}
                  />
                )}
                {/* Price chip overlay — admin-editable per slide (Home Editor → Hero Slide → From price) */}
                {(active.from_price || active.from_price === 0) && (
                  <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#111111] shadow-sm rounded-full">
                    From ₹{active.from_price}
                  </div>
                )}
                {/* Mobile: swipe affordance — paired prev/next chevrons that change the slide without navigating away.
                    stopPropagation + preventDefault on the wrapping Link, then advance idx. */}
                {n > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx((cur) => (cur + n - 1) % n); }}
                      aria-label="Previous slide"
                      data-testid="hero-prev"
                      className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white"
                    >
                      <ChevronLeft size={18} className="text-[#111111]" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx((cur) => (cur + 1) % n); }}
                      aria-label="Next slide"
                      data-testid="hero-next"
                      className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white"
                    >
                      <ChevronRight size={18} className="text-[#111111]" />
                    </button>
                  </>
                )}
              </Link>
              {/* 3 small tiles — DESKTOP ONLY (hidden on mobile per Option A). Click changes the active slide.
                  Mobile users swipe or tap dots instead. */}
              {[1, 2, 3].map((i) => {
                const tile = collage[i] || collage[0];
                if (!tile) return <div key={i} className="hidden md:block bg-zinc-100 rounded-2xl" />;
                const spanClass = i === 1 ? "md:col-span-1 md:row-span-2" : "md:col-span-1 md:row-span-1";
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx((idx + i) % n)}
                    data-testid={`hero-collage-${i}`}
                    aria-label={`Switch to slide ${((idx + i) % n) + 1}`}
                    className={`hidden md:block relative ${spanClass} overflow-hidden bg-zinc-100 group cursor-pointer rounded-2xl`}
                  >
                    <CrossfadeImage
                      src={tile.image}
                      alt={tile.alt || tile.title || "Mockup"}
                      className="absolute inset-0 w-full h-full"
                      imgClassName="transition-transform duration-500 group-hover:scale-105"
                      duration={700}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * AIDesignPromoStrip — "Design in 30 Seconds with AI" attention pull.
 *
 * Renders as a full-width strip on both mobile + desktop. All copy is
 * admin-editable via /admin/home-editor → the standard `eyebrow / heading /
 * sub / cta_label / cta_url` fields (plus an optional `highlight` sub-string
 * of the heading that gets the orange accent — leave blank to disable).
 * Backed by `home-ai-design-promo` test-id so QA + analytics can pin the
 * impression.
 */
function AIDesignPromoStrip({ cfg = {} }) {
  const eyebrow = cfg.eyebrow || "New · AI Studio";
  const heading = cfg.heading || "Design in 30 seconds with AI";
  const sub = cfg.sub || "Type your idea → get 300-DPI print-ready art. No design skills needed.";
  const ctaLabel = cfg.cta_label || "Try Now";
  const ctaUrl = cfg.cta_url || "/customize?tab=ai&skip_welcome=1";
  const highlight = cfg.highlight || "";

  // Split `heading` around `highlight` so we can wrap the matched span in
  // an orange accent. Falls back to plain heading when `highlight` is blank
  // or not found (case-insensitive match).
  let headingNodes;
  if (highlight) {
    const idx = heading.toLowerCase().indexOf(highlight.toLowerCase());
    if (idx >= 0) {
      const before = heading.slice(0, idx);
      const match = heading.slice(idx, idx + highlight.length);
      const after = heading.slice(idx + highlight.length);
      headingNodes = (
        <>
          {before}<span className="text-[#FF5722]">{match}</span>{after}
        </>
      );
    } else {
      headingNodes = heading;
    }
  } else {
    headingNodes = heading;
  }

  return (
    <section className="bg-white pt-4 md:pt-6" data-testid="home-ai-design-promo-section">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <Link
          to={ctaUrl}
          data-testid="home-ai-design-promo"
          onClick={() => trackClick({ section: "hero", type: "ai_design_promo", label: heading, url: ctaUrl })}
          className="group block relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0f0f0f] via-[#1c1c1c] to-[#111111] text-white border border-zinc-800 shadow-lg hover:shadow-2xl hover:border-[#FF5722]/50 transition-all duration-300"
        >
          {/* animated glow blobs */}
          <div aria-hidden="true" className="absolute -top-12 -left-10 w-40 h-40 rounded-full bg-[#FF5722]/30 blur-3xl pointer-events-none animate-pulse" />
          <div aria-hidden="true" className="absolute -bottom-16 right-1/3 w-48 h-48 rounded-full bg-orange-400/20 blur-3xl pointer-events-none" />
          {/* subtle grain */}
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "8px 8px" }} />

          <div className="relative flex items-center gap-3 sm:gap-4 md:gap-6 px-4 sm:px-6 md:px-8 py-4 md:py-5">
            {/* Left — animated sparkle badge */}
            <div className="shrink-0 relative">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-[#FF5722] to-[#E64A19] inline-flex items-center justify-center shadow-lg shadow-[#FF5722]/40 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                <Wand2 size={22} className="text-white" strokeWidth={2.5} />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                <Sparkles size={9} className="text-[#FF5722]" strokeWidth={3} />
              </div>
            </div>

            {/* Middle — copy */}
            <div className="flex-1 min-w-0">
              {eyebrow && (
                <div className="inline-flex items-center gap-1.5 bg-[#FF5722]/15 text-[#FF7043] text-[9px] md:text-[10px] uppercase tracking-[0.2em] font-black px-2 py-0.5 rounded">
                  <Sparkles size={10} strokeWidth={3} /> {eyebrow}
                </div>
              )}
              <div className="font-display font-black text-lg sm:text-xl md:text-2xl tracking-tight leading-[1.15] mt-1">
                {headingNodes}
              </div>
              {sub && (
                <div className="text-[11px] sm:text-xs md:text-sm text-white/70 mt-0.5 md:mt-1 hidden sm:block">
                  {sub}
                </div>
              )}
            </div>

            {/* Right — CTA pill */}
            {ctaLabel && (
              <div className="shrink-0">
                <span className="inline-flex items-center gap-1.5 bg-white text-[#111111] font-black text-[11px] md:text-xs uppercase tracking-wider px-3 md:px-5 h-9 md:h-11 rounded-full group-hover:bg-[#FF5722] group-hover:text-white transition-colors duration-200 shadow-md">
                  <span className="hidden sm:inline">{ctaLabel}</span>
                  <span className="sm:hidden">{ctaLabel.split(" ")[0]}</span>
                  <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>
    </section>
  );
}

// -------------------- ANNOUNCE BAR --------------------
function AnnounceBar({ cfg }) {
  if (!cfg?.text) return null;
  return (
    <div className="bg-[#FF5722] text-white py-2.5 text-center text-xs font-bold tracking-wider" data-testid="announce-bar">
      <div className="max-w-7xl mx-auto px-4">
        {cfg.url ? <Link to={cfg.url} className="hover:text-[#FF5722]">{cfg.text} →</Link> : cfg.text}
      </div>
    </div>
  );
}

// -------------------- PARTNERS --------------------
function PartnersStrip({ cfg }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  // Normalise field names — backend uses logo_url + website_url
  const partners = items
    .map((p) => ({ id: p.id, name: p.name, image: p.image || p.logo_url, url: p.url || p.website_url }))
    .filter((p) => p.image);
  if (!partners.length) return null;
  return (
    <section className="bg-[#111111] text-white py-8 md:py-10" data-testid="partners-strip">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-center gap-6 md:gap-10 flex-wrap md:flex-nowrap">
          <div className="flex-shrink-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[#FF5722]">
              {cfg?.eyebrow || "Trusted by 1,200+ brands"}
            </div>
            <div className="mt-1 font-display font-black text-xl md:text-2xl tracking-tighter leading-none">
              {cfg?.heading || "Teams across India."}
            </div>
          </div>
          {/* Vertical accent bar — Bewakoof-style brand divider */}
          <div className="hidden md:block flex-shrink-0 h-12 w-1 bg-[#FF5722] rounded-full" aria-hidden="true" />
          <div className="flex-1 flex gap-10 md:gap-14 items-center overflow-x-auto no-scrollbar">
            {partners.slice(0, 12).map((p) => (
              <a key={p.id} href={p.url || "#"} target={p.url ? "_blank" : "_self"} rel="noreferrer" className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity">
                <img loading="lazy" decoding="async"
                  src={proxify(p.image, 320)}
                  srcSet={proxifySrcSet(p.image, [160, 320])}
                  sizes="120px"
                  alt={p.name}
                  width="120"
                  height="48"
                  onError={(e) => { e.target.style.display = "none"; }}
                  className="h-10 md:h-12 w-auto object-contain brightness-0 invert"
                />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------- TRUST STRIP (compact band) --------------------
function TrustStrip({ cfg }) {
  const items = cfg?.items || [];
  if (!items.length) return null;
  return (
    <section className="bg-zinc-50 border-y border-zinc-200" data-testid="trust-strip">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 md:py-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {items.map((it, i) => {
            const Icon = ICONS[it.icon] || ShieldCheck;
            return (
              <div key={i} className="flex items-center gap-2.5" data-testid={`trust-${i}`}>
                <div className="w-9 h-9 bg-white border border-zinc-200 text-[#FF5722] flex items-center justify-center flex-shrink-0 rounded-full">
                  <Icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[13px] text-[#111111] leading-tight">{it.label}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight truncate">{it.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -------------------- FEATURED CATEGORIES (Shop by Vibe — bold tile grid) --------------------
function FeaturedCategories({ cfg, cats }) {
  const impressionRef = useImpressionTracker("featured_categories");
  const { gender } = useGender();
  // Per-gender curated lists override the global single list when present.
  // Schema: cfg.category_slugs_by_gender = { men: [], women: [], couple: [] }
  // Falls back to cfg.category_slugs (legacy) if a given gender's list is empty.
  const byGender = cfg?.category_slugs_by_gender || {};
  const genderSlugs = (byGender[gender] || []).filter(Boolean);
  const legacySlugs = cfg?.category_slugs || [];
  const slugs = genderSlugs.length > 0 ? genderSlugs : legacySlugs;
  const list = slugs.length
    ? slugs.map((sl) => cats.find((c) => c.slug === sl)).filter(Boolean)
    : cats.slice(0, 12);
  if (!list.length) return null;
  const heading = cfg?.heading || "Shop by vibe";
  const eyebrow = cfg?.eyebrow || "Find your aesthetic";
  return (
    <section ref={impressionRef} className="bg-white py-6 md:py-10" data-testid="featured-categories">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5722] font-black">{eyebrow}</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#111111]">
              {heading}.
            </h2>
            {cfg?.sub && <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-md">{cfg.sub}</p>}
          </div>
          <Link to="/shop" data-testid="featured-categories-view-all" className="text-xs font-black text-[#111111] hover:text-[#FF5722] inline-flex items-center gap-1.5 uppercase tracking-[0.18em] border-b-2 border-[#111111] hover:border-[#FF5722] pb-0.5 transition-colors">
            View all <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {list.slice(0, 12).map((c) => (
            <Link
              key={c.id || c.slug}
              to={`/shop?category=${c.slug}`}
              onClick={() => trackClick("featured_categories", `/shop?category=${c.slug}`, c.slug)}
              data-testid={`home-category-${c.slug}`}
              className="group relative overflow-hidden bg-zinc-100 aspect-[2/3] sm:aspect-[5/7] rounded-2xl"
            >
              {c.image && (
                <img
                  decoding="async"
                  src={proxify(c.image, 560)}
                  srcSet={proxifySrcSet(c.image, [360, 560, 720])}
                  sizes="(max-width: 768px) 50vw, 280px"
                  alt={c.name}
                  loading="lazy"
                  width="400"
                  height="560"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              )}
              {/* Always-on dark strip at the bottom for legibility */}
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none" />
              {/* Hover orange tint */}
              <div className="absolute inset-0 bg-[#FF5722]/0 group-hover:bg-[#FF5722]/15 transition-colors pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-white">
                <div className="font-display font-black tracking-tight text-base md:text-lg leading-tight line-clamp-2 uppercase">
                  {c.name}
                </div>
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.16em] text-white/85 group-hover:text-white">
                  Shop now <ArrowRight size={10} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------------- FEATURED PRODUCTS --------------------
function FeaturedProducts({ cfg, products }) {
  const impressionRef = useImpressionTracker("featured_products");
  const { gender } = useGender();
  const featuredDrag = useDragGuard();
  // Per-gender curated lists override the global single list when present.
  // Schema: cfg.product_slugs_by_gender = { men: [], women: [], couple: [] }
  // Falls back to cfg.product_slugs (legacy) if a given gender's list is empty.
  const byGender = cfg?.product_slugs_by_gender || {};
  const genderSlugs = (byGender[gender] || []).filter(Boolean);
  const legacySlugs = cfg?.product_slugs || [];
  const slugs = genderSlugs.length > 0 ? genderSlugs : legacySlugs;
  const fromApi = products?.featured || products?.best_sellers || [];
  const [curated, setCurated] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!slugs.length) { setCurated(null); return; }
    api.get("/catalog/products-by-slugs", { params: { slugs: slugs.join(",") } })
      .then((r) => { if (!cancelled) setCurated(r.data.products || []); })
      .catch(() => { if (!cancelled) setCurated([]); });
    return () => { cancelled = true; };
  }, [slugs.join("|")]); // re-fetch when admin changes the curated list

  const filtered = slugs.length
    ? (curated || [])
    : fromApi.slice(0, cfg?.limit || 8);
  if (!filtered.length && slugs.length && curated === null) {
    // Still loading curated — render heading area only
    return (
      <SectionShell testId="featured-products" className="bg-[#F9FAFB]">
        <div className="max-w-xl">
          <Eyebrow>{cfg?.eyebrow || "Hand-picked"}</Eyebrow>
          <H2 className="mt-3">{cfg?.heading || "Featured this week"}</H2>
        </div>
      </SectionShell>
    );
  }
  if (!filtered.length) return null;
  const heading = cfg?.heading || "Trending now";
  const eyebrow = cfg?.eyebrow || "Most-loved this week";
  return (
    <section ref={impressionRef} className="bg-white py-10 md:py-14" data-testid="featured-products">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5722] font-black">{eyebrow}</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#111111]">
              {heading}.
            </h2>
            {cfg?.sub && <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-md">{cfg.sub}</p>}
          </div>
          <Link to="/shop" data-testid="featured-products-view-all" className="text-xs font-black text-[#111111] hover:text-[#FF5722] inline-flex items-center gap-1.5 uppercase tracking-[0.18em] border-b-2 border-[#111111] hover:border-[#FF5722] pb-0.5 transition-colors">
            View catalog <ArrowRight size={13} />
          </Link>
        </div>
        {/* Horizontal scroller — snap-x on touch, native CSS scrollbar hidden.
            Cards keep ProductCard styling but are widened so prices read
            instantly on the strip (Bewakoof-style "Trending Now" UX).
            Right-edge fade + chevron indicator hint there's more to swipe. */}
        <div className="relative" data-testid="featured-products-scroller-wrap">
          <div
            {...featuredDrag}
            className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
            data-testid="featured-products-scroller"
          >
            {filtered.map((p) => (
              <div
                key={p.id}
                className="snap-start flex-shrink-0 w-[62%] sm:w-[42%] md:w-[28%] lg:w-[22%]"
              >
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          {/* Right-edge fade — disappears smoothly into white on hover when
              the user starts engaging. Hidden from screen readers. */}
          {filtered.length > 2 && (
            <>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute top-0 right-0 h-full w-24 bg-gradient-to-l from-white via-white/80 to-transparent hidden md:block"
              />
              <div
                aria-hidden="true"
                className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-2 border-zinc-200 shadow-md text-zinc-700 flex items-center justify-center pointer-events-none animate-pulse hidden md:flex"
              >
                <ArrowRight size={18} />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// -------------------- WHY XYZPLUS — inline feature strip --------------------
/**
 * Browse by Use Case — bento grid with intent-driven entry points.
 * Per design agent: B2B/Bulk buyers shop by intent ("we need college fest merch"),
 * not generic apparel categories. Converts significantly better for hybrid custom merch.
 */
function UseCaseGrid({ cfg = {} }) {
  const { gender } = useGender();
  const defaultCases = [
    { key: "corporate", title: "Corporate Gifts", sub: "Onboarding kits, swag boxes, founder drops",
      img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=900&q=70",
      cta: "/custom-shop?use_case=corporate", big: true },
    { key: "fest", title: "College Fests", sub: "Sweatshirts · totes · ID lanyards",
      img: "https://images.unsplash.com/photo-1525974160448-038dacadcc71?auto=format&fit=crop&w=600&q=70",
      cta: "/custom-shop?use_case=fest" },
    { key: "sports", title: "Team Jerseys", sub: "Sublimated · embroidered · numbered",
      img: "https://images.unsplash.com/photo-1556906781-9a412961c28c?auto=format&fit=crop&w=600&q=70",
      cta: "/custom-shop?use_case=team" },
    { key: "wedding", title: "Wedding Squad", sub: "Bride tribe · groomsmen tees · custom",
      img: "https://images.unsplash.com/photo-1583394838336-acd977736f90?auto=format&fit=crop&w=600&q=70",
      cta: "/custom-shop?use_case=wedding" },
  ];
  // Per-gender override beats the legacy global `cases` array.
  // Schema: cfg.cases_by_gender = { men: [], women: [], couple: [] }
  const byGender = (cfg?.cases_by_gender || {})[gender] || [];
  const globalCases = cfg?.cases || [];
  let cases = byGender.length > 0
    ? byGender
    : (globalCases.length > 0 ? globalCases : defaultCases);
  // Auto-promote the first tile to the "big" mega-tile when the admin hasn't
  // explicitly marked one. Restores the magazine layout for per-gender lists
  // where admins typically just upload images without remembering the `big`
  // flag. Only mutates when zero items are big.
  if (cases.length > 0 && !cases.some((c) => c && c.big)) {
    cases = cases.map((c, i) => (i === 0 ? { ...c, big: true } : c));
  }
  const eyebrow = cfg.eyebrow || "Browse by use case";
  const heading = cfg.heading || "Design for every occasion.";
  const sub = cfg.sub || "From startup swag to wedding squads.";
  const viewAllUrl = cfg.view_all_url || "/custom-shop";
  return (
    <section ref={useImpressionTracker("use_cases")} className="bg-white" data-testid="section-use-cases">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4">
          <div>
            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-bold text-[#FF5722] mb-2">{eyebrow}</div>
            <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl text-[#111111] tracking-tight leading-[1.05]">
              {heading}<br />
              <span className="text-zinc-500 font-bold">{sub}</span>
            </h2>
          </div>
          <Link to={viewAllUrl} className="hidden md:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5722] transition-colors">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {cases.map((c) => (
            <Link
              key={c.key || c.title}
              to={c.cta || "/custom-shop"}
              data-testid={`use-case-${c.key || c.title}`}
              className={`relative overflow-hidden rounded-2xl group isolate ${c.big ? "col-span-2 aspect-[2/1] md:col-span-2 md:row-span-2 md:aspect-auto" : "aspect-square"}`}
            >
              <img src={proxify(c.img, 768)} srcSet={proxifySrcSet(c.img, [480, 768, 1024])} sizes="(max-width: 768px) 50vw, 33vw" alt={c.title} loading="lazy" decoding="async" width="640" height="640"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {/* Overlay text — conditional. Admins can blank out `title` & `sub`
                  to ship pure-image tiles (no gradient either). */}
              {(c.title || c.sub) && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end text-white">
                    {c.title && (
                      <div className={`font-display font-black tracking-tight ${c.big ? "text-2xl md:text-4xl" : "text-base md:text-xl"}`}>{c.title}</div>
                    )}
                    {c.sub && (
                      <div className={`opacity-90 mt-1 ${c.big ? "text-sm md:text-base" : "text-[11px] md:text-xs"}`}>{c.sub}</div>
                    )}
                    <div className="mt-2 md:mt-3 flex items-center gap-1 text-[10px] md:text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                      Start designing <ArrowRight size={12} />
                    </div>
                  </div>
                </>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * BulkTiersStrip — visualise volume-pricing tiers as horizontal pill bars.
 */
function BulkTiersStrip({ cfg = {} }) {
  const site = useSiteSettings();
  const defaults = [
    { min: 12,  discount_pct: 0,  label: "Starter" },
    { min: 25,  discount_pct: 5,  label: "Squad" },
    { min: 50,  discount_pct: 10, label: "Team", highlight: true },
    { min: 100, discount_pct: 18, label: "Company" },
    { min: 250, discount_pct: 25, label: "Enterprise" },
  ];
  // Prefer live tiers from admin settings (source of truth for actual
  // checkout discount). Map min_qty/pct/is_popular → min/discount_pct/highlight.
  const liveTiers = (site?.bulk_discount_tiers || []).map((t) => ({
    min: Number(t.min_qty) || 0,
    discount_pct: Number(t.pct) || 0,
    label: t.label || `${t.min_qty}+`,
    highlight: !!t.is_popular,
  }));
  // Live admin settings are the source of truth for actual checkout discount,
  // so always prefer them when present. Fall back to section-level cfg.tiers
  // (legacy override) and finally to hardcoded defaults.
  const tiers = liveTiers.length > 0
    ? liveTiers
    : (cfg.tiers && cfg.tiers.length > 0 ? cfg.tiers : defaults);
  const eyebrow = cfg.eyebrow || "Volume pricing";
  const heading = cfg.heading || "More pieces. Bigger savings.";
  const sub = cfg.sub || "Transparent bulk tiers — auto-applied at checkout.";
  const ctaLabel = cfg.cta_label || "Request a quote";
  const ctaUrl = cfg.cta_url || "/contact?intent=bulk";
  // Orange intensity gradient: light → dark as discount grows.
  const tints = [
    "bg-[#FFE7DC] text-[#FF5722]",
    "bg-[#FFB791] text-white",
    "bg-[#FF7A47] text-white",
    "bg-[#FF5722] text-white",
    "bg-[#D9420F] text-white",
  ];
  return (
    <section className="bg-white py-6 md:py-10" data-testid="section-bulk-tiers">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5722] font-black">{eyebrow}</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#111111]">
              {heading}
            </h2>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-md">{sub}</p>
          </div>
          <Link
            to={ctaUrl}
            data-testid="bulk-tiers-cta"
            className="inline-flex items-center gap-2 bg-[#111111] text-white text-sm font-black uppercase tracking-wider px-6 h-10 rounded-md hover:bg-[#FF5722] transition-colors whitespace-nowrap"
          >
            {ctaLabel} <ArrowRight size={14} />
          </Link>
        </div>

        {/* Bold orange ladder — 5 tiles, solid colour intensity escalates with savings */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4"
          data-testid="bulk-tiers-list"
        >
          {tiers.map((t, i) => {
            const isHi = t.highlight;
            const tint = tints[Math.min(i, tints.length - 1)];
            return (
              <div
                key={`${t.min}-${i}`}
                data-testid={`bulk-tier-${t.min}`}
                className={`relative overflow-hidden rounded-2xl p-5 md:p-6 ${tint} transition-transform hover:-translate-y-0.5`}
              >
                {/* Subtle diagonal stripe pattern */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 opacity-[0.08] pointer-events-none"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 12px)",
                  }}
                />
                <div className="relative">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] opacity-80">
                    {t.label}
                  </div>
                  <div className="mt-2 font-display font-black text-4xl md:text-5xl tracking-tighter leading-[0.9]">
                    {t.min}+
                  </div>
                  <div className="text-xs font-bold opacity-85 mt-1">pieces</div>
                  <div className="mt-4 font-display font-black text-2xl md:text-3xl tracking-tighter leading-none">
                    {t.discount_pct > 0 ? `${t.discount_pct}% OFF` : "Base"}
                  </div>
                  {isHi && (
                    <div className="mt-3 inline-flex items-center gap-1 bg-white/95 text-[#FF5722] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                      ★ Most popular
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * StudioDemoLoop — visual showcase of the Custom Studio with a static
 * design-canvas mockup + bullet list + CTA. (A future improvement could
 * embed a real video loop or canvas screenshot here.)
 */
function StudioDemoLoop({ cfg = {} }) {
  const eyebrow = cfg.eyebrow || "Custom studio";
  const heading = cfg.heading || "Drag, drop, design. Done.";
  const sub = cfg.sub || "Powerful enough for pros. Friendly enough for first-timers.";
  const image = cfg.image || "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&w=1400&q=80";
  const bullets = (cfg.bullets && cfg.bullets.length > 0) ? cfg.bullets : [
    "AI design generation from a text prompt",
    "Embroidery + Print mode with live previews",
    "Auto-resize to every t-shirt size",
    "300-DPI proof on the actual mockup",
  ];
  const ctaLabel = cfg.cta_label || "Try the Custom Studio";
  const ctaUrl = cfg.cta_url || "/custom-shop";
  return (
    <section className="bg-[#111111] text-white" data-testid="section-studio-demo">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-14 items-center">
          <div className="relative rounded-2xl overflow-hidden border border-zinc-800 shadow-[0_24px_80px_-24px_rgba(255,87,34,0.5)] aspect-[4/3] lg:aspect-[5/4]">
            <img
              src={proxify(image, 1024)}
              srcSet={proxifySrcSet(image, [640, 1024, 1280])}
              sizes="(max-width: 768px) 100vw, 50vw"
              alt="Custom Studio preview"
              loading="lazy"
              decoding="async"
              width="1024"
              height="768"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-[#FF5722]/20" />
            {/* Tiny status chip in corner */}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF5722] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FF5722]" />
              </span>
              <span>Live preview</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#FF5722] mb-2">{eyebrow}</div>
            <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{heading}</h2>
            <p className="mt-3 text-zinc-300 text-base max-w-lg">{sub}</p>
            <ul className="mt-6 space-y-2.5" data-testid="studio-demo-bullets">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-zinc-200">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#FF5722] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link
              to={ctaUrl}
              data-testid="studio-demo-cta"
              className="inline-flex items-center gap-2 mt-7 bg-[#FF5722] text-white font-bold px-5 h-11 rounded-[10px] hover:bg-orange-600 transition-colors text-sm"
            >
              {ctaLabel} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


// -------------------- HOT DEALS BANNER (Bewakoof-style 3-up bold promo strip) --------------------
// Sits right below the hero. Three solid-color cards with HUGE display
// typography announcing the 3 most-loved store offers. Replaces the old
// "WhyStrip" USP icon row (which was redundant with the CMS trust_strip).
// Hard-coded for visual impact — admin still controls policy copy via
// AnnounceBar + AnnounceBanner CMS slots if specific campaigns need to swap.
function HotDealsBanner({ cfg }) {
  const { gender } = useGender();
  const hotDealsDrag = useDragGuard();
  const fallback = [
    {
      cta_url: "/shop?tag=bulk",
      eyebrow: "BULK ORDERS",
      headline: "FLAT 20% OFF",
      sub: "On orders of 12 pieces or more",
      bg_color: "#FF5722",
      text_color: "#FFFFFF",
      cta_label: "Shop bulk",
    },
    {
      cta_url: "/custom",
      eyebrow: "DESIGN FREE",
      headline: "₹0 PROOF",
      sub: "Unlimited revisions · 5-day turnaround",
      bg_color: "#111111",
      text_color: "#FFFFFF",
      cta_label: "Start designing",
    },
    {
      cta_url: "/shop?tag=combo",
      eyebrow: "COUPLE COMBO",
      headline: "BUY 2 GET 10% OFF",
      sub: "Auto-applied at checkout on matched sets",
      bg_color: "#FFE7DC",
      text_color: "#111111",
      cta_label: "Shop couple",
    },
  ];
  // Per-gender override beats the legacy global `deals` array.
  // Schema: cfg.deals_by_gender = { men: [], women: [], couple: [] }
  const byGender = (cfg?.deals_by_gender || {})[gender] || [];
  const globalDeals = cfg?.deals || [];
  const deals = byGender.length > 0
    ? byGender
    : (globalDeals.length > 0 ? globalDeals : fallback);
  return (
    <section ref={useImpressionTracker("hot_deals")} className="bg-white py-4 md:py-6" data-testid="hot-deals-banner">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        {/* Desktop: 3-up grid. Mobile: horizontal snap-scroll with peek. */}
        <div className="hidden md:grid md:grid-cols-3 gap-3 md:gap-4">
          {deals.map((d, i) => <HotDealCard key={i} {...d} />)}
        </div>
        <div
          {...hotDealsDrag}
          className="md:hidden flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1"
          data-testid="hot-deals-scroller-mobile"
        >
          {deals.map((d, i) => (
            <div key={i} className="snap-start flex-shrink-0 w-[85vw] max-w-[420px]">
              <HotDealCard {...d} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotDealCard({ cta_url, eyebrow, headline, sub, bg_color, text_color, cta_label }) {
  // Compute perceptual luminance so we correctly classify mid-saturation
  // colors like orange (#FF5722) as "dark" — the previous R-only heuristic
  // mis-flagged them as light, making the hover pill blend into the card.
  // Formula: rec601 luma (Y') = 0.299R + 0.587G + 0.114B.
  const hex = (bg_color || "#FF5722").replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16) || 0;
  const g = parseInt(hex.slice(2, 4), 16) || 0;
  const b = parseInt(hex.slice(4, 6), 16) || 0;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  const isDark = luminance < 160;
  // Detect orange / red family backgrounds so the hover pill never collides
  // with the brand-orange (#FF5722) hover color we use everywhere else.
  const isOrangeFamily = r > 200 && g < 150 && b < 110;
  // Dark / saturated cards: white pill initial. Orange/red family flips to
  // BLACK on hover (always contrasts). Other dark cards flip to brand orange.
  // Light / cream cards: black pill flips to brand orange.
  let ctaCls;
  if (isDark) {
    ctaCls = isOrangeFamily
      ? "bg-white text-[#111111] hover:bg-[#111111] hover:text-white"
      : "bg-white text-[#111111] hover:bg-[#FF5722] hover:text-white";
  } else {
    ctaCls = "bg-[#111111] text-white hover:bg-[#FF5722] hover:text-white";
  }
  return (
    <Link
      to={cta_url || "/shop"}
      onClick={() => trackClick("hot_deals", cta_url || "/shop", headline)}
      data-testid={`hot-deal-${(headline || "deal").toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
      className="group relative block overflow-hidden rounded-2xl p-5 md:p-7 transition-transform hover:-translate-y-0.5"
      style={{ backgroundColor: bg_color || "#FF5722", color: text_color || "#FFFFFF" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 14px)",
        }}
      />
      <div className="relative">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] opacity-80">
          {eyebrow}
        </div>
        <div className="mt-2 font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
          {headline}
        </div>
        <div className="mt-2 text-xs md:text-sm opacity-85 leading-snug max-w-[90%]">
          {sub}
        </div>
        <div
          className={`mt-4 md:mt-5 inline-flex items-center gap-1.5 h-10 px-6 rounded-md text-sm font-black uppercase tracking-wider transition-colors ${ctaCls}`}
        >
          {cta_label || "Shop now"} <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}

// -------------------- HOW IT WORKS (tight horizontal strip) --------------------
function HowItWorks({ cfg }) {
  const { gender } = useGender();
  // Per-gender override beats the legacy global `steps` array.
  // Schema: cfg.steps_by_gender = { men: [], women: [], couple: [] }
  const byGender = (cfg?.steps_by_gender || {})[gender] || [];
  const globalSteps = cfg?.steps || [];
  const steps = byGender.length > 0 ? byGender : globalSteps;
  if (!steps.length) return null;
  return (
    <section className="bg-zinc-50 py-8 md:py-12" data-testid="how-it-works">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5722] font-black">{cfg?.eyebrow || "How it works"}</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#111111]">
              {cfg?.heading || "From idea to delivery."}
            </h2>
          </div>
          <Link to="/custom" data-testid="how-it-works-cta" className="text-xs font-black text-[#111111] hover:text-[#FF5722] inline-flex items-center gap-1.5 uppercase tracking-[0.18em] border-b-2 border-[#111111] hover:border-[#FF5722] pb-0.5 transition-colors">
            Start designing <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {steps.map((step, i) => (
            <div
              key={i}
              data-testid={`how-step-${i}`}
              className="group bg-white border-2 border-zinc-100 hover:border-[#FF5722] transition-colors p-5 md:p-6 rounded-2xl"
            >
              <div className="font-display font-black text-5xl md:text-6xl tracking-tighter text-zinc-200 group-hover:text-[#FF5722] leading-none transition-colors">
                {String(step.n || i + 1).padStart(2, "0")}
              </div>
              <div className="mt-4 font-display font-black text-lg md:text-xl text-[#111111] leading-tight tracking-tight uppercase">{step.label}</div>
              <div className="mt-1.5 text-xs text-zinc-500 leading-relaxed line-clamp-3">{step.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------------- TESTIMONIALS (compact, bold) --------------------
function Testimonials({ cfg }) {
  const { gender } = useGender();
  // Per-gender override beats the legacy global `items` array.
  // Schema: cfg.items_by_gender = { men: [], women: [], couple: [] }
  const byGender = (cfg?.items_by_gender || {})[gender] || [];
  const globalItems = cfg?.items || [];
  const items = byGender.length > 0 ? byGender : globalItems;
  const [idx, setIdx] = useState(0);
  // Reset active quote when the gender (and therefore list) changes — otherwise
  // a stale index can point to a non-existent slot when lists differ in length.
  useEffect(() => { setIdx(0); }, [gender, items.length]);
  if (!items.length) return null;
  const t = items[idx] || items[0];
  return (
    <section className="bg-white py-8 md:py-12" data-testid="testimonials">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 md:mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5722] font-black">{cfg?.eyebrow || "What teams say"}</div>
            <h2 className="mt-2 font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#111111] max-w-xl">
              {cfg?.heading || "Loved by teams across India."}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-[#FF5722]">
            {[0,1,2,3,4].map((i) => <Star key={i} size={16} fill="#FF5722" strokeWidth={0} />)}
            <span className="ml-1 text-xs font-black text-[#111111]">4.9 / 5 · 1,200+ reviews</span>
          </div>
        </div>
        <div className="bg-[#111111] text-white rounded-3xl p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <blockquote className="lg:col-span-9 font-display text-xl md:text-2xl lg:text-3xl tracking-tight leading-snug">
            <span className="text-[#FF5722]">&ldquo;</span>{t.quote}<span className="text-[#FF5722]">&rdquo;</span>
          </blockquote>
          <div className="lg:col-span-3 lg:border-l border-white/15 lg:pl-6">
            <div className="font-black text-base">{t.author}</div>
            <div className="text-xs text-white/65 mt-0.5">{t.role}</div>
            {items.length > 1 && (
              <div className="mt-5 flex gap-1.5">
                {items.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Testimonial ${i + 1}`}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#FF5722]" : "w-3 bg-white/25 hover:bg-white/45"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------- CTA STRIP — bold full-bleed orange band --------------------
function CtaStrip({ cfg }) {
  const { gender } = useGender();
  // Per-gender variant overrides individual fields on the root config.
  // Schema: cfg.variants_by_gender = { men: {eyebrow, heading, sub, cta_label, cta_url, secondary_cta_label, secondary_cta_url}, women: {}, couple: {} }
  // An empty string OR missing key inside a variant falls through to the root cfg.
  const variant = (cfg?.variants_by_gender || {})[gender] || {};
  const pick = (k) => (variant[k] ?? "") !== "" ? variant[k] : cfg?.[k];
  const eyebrow = pick("eyebrow");
  const heading = pick("heading");
  const sub = pick("sub");
  const cta_label = pick("cta_label");
  const cta_url = pick("cta_url");
  const secondary_cta_label = pick("secondary_cta_label");
  const secondary_cta_url = pick("secondary_cta_url");
  return (
    <section ref={useImpressionTracker("cta_strip")} className="py-8 md:py-12" data-testid="cta-strip">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div
          className="relative overflow-hidden bg-[#FF5722] text-white rounded-3xl p-8 md:p-14"
          data-testid="bulk-cta"
        >
          {/* Subtle abstract shapes — pure CSS, no extra assets */}
          <div aria-hidden="true" className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-24 left-1/3 w-60 h-60 rounded-full bg-black/10 blur-2xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-8">
              {eyebrow && (
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/85 font-black">{eyebrow}</div>
              )}
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                {heading || "Outfitting 12 or 1,200?"}
              </h2>
              {sub && <p className="mt-4 text-white/90 text-base lg:text-lg leading-relaxed max-w-xl">{sub}</p>}
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col lg:items-end gap-3">
              <Link
                to={cta_url || "/contact"}
                data-testid="cta-primary"
                className="w-56 h-10 inline-flex items-center justify-center gap-2 px-4 rounded-md bg-white text-[#111111] text-sm font-black uppercase tracking-wider hover:bg-[#111111] hover:text-white transition-colors whitespace-nowrap"
              >
                {cta_label || "Request a Quote"} <ArrowRight size={14} />
              </Link>
              {secondary_cta_label && (
                <Link
                  to={secondary_cta_url || "/catalog"}
                  data-testid="cta-secondary"
                  className="w-56 h-10 inline-flex items-center justify-center gap-2 px-4 rounded-md border-2 border-white text-white text-sm font-black uppercase tracking-wider hover:bg-white hover:text-[#FF5722] transition-colors whitespace-nowrap"
                >
                  {secondary_cta_label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------- CUSTOM HTML --------------------
function CustomHtml({ cfg }) {
  if (!cfg?.html) return null;
  // CMS-supplied HTML — sanitized so even a compromised admin can't ship
  // XSS to every storefront visitor.
  return (
    <SectionShell testId="custom-html">
      <div dangerouslySetInnerHTML={{ __html: sanitizeRichText(cfg.html) }} />
    </SectionShell>
  );
}

// -------------------- REFERRAL PROMO BANNER (bold black band) --------------------
function ReferralPromoBanner({ cfg }) {
  const eyebrow = cfg?.eyebrow || "Refer & Earn";
  const heading = cfg?.heading || "Refer a friend. Both get ₹200 off.";
  const sub = cfg?.sub || "Auto-credited the moment your friend signs up — no purchase needed. Points expire in 60 days.";
  const ctaLabel = cfg?.cta_label || "Get your link";
  const ctaUrl = cfg?.cta_url || "/refer-and-earn";
  return (
    <section className="bg-white" data-testid="home-referral-promo">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 md:py-6">
        <Link
          to={ctaUrl}
          data-testid="home-referral-cta"
          className="group relative block overflow-hidden bg-[#111111] text-white rounded-2xl p-5 md:p-7"
        >
          {/* Diagonal stripe texture */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 14px)",
            }}
          />
          {/* Orange blob accent */}
          <div aria-hidden="true" className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#FF5722]/30 blur-3xl pointer-events-none" />

          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 flex-shrink-0 rounded-full bg-[#FF5722] flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[#FF5722]">
                  {eyebrow}
                </div>
                <div className="mt-1 font-display font-black text-xl sm:text-2xl md:text-3xl tracking-tighter leading-[0.95]">
                  {heading}
                </div>
                <div className="mt-1.5 text-xs sm:text-sm text-white/70 max-w-md">
                  {sub}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 inline-flex items-center gap-2 bg-[#FF5722] text-white text-sm font-black uppercase tracking-wider px-6 h-10 rounded-md group-hover:bg-white group-hover:text-[#FF5722] transition-colors whitespace-nowrap" data-testid="referral-get-link-button">
              {ctaLabel} <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}


/**
 * Home-page AI Virtual Try-On promo.
 *
 * Wedged in just after the Use-Case Grid — far enough down that browsers have
 * committed to the brand, close enough to a category click to convert. Two-
 * column on desktop: a magazine-style photo collage on the left, value-prop
 * + 3-step explainer + CTA on the right. Mobile stacks naturally.
 *
 * Click → /shop with `?tryon=1` so the catalog can highlight tryon-eligible
 * items (future-friendly query — even if unhandled today, lands on Shop).
 */
function TryOnPromoBanner({ cfg: sectionCfg = {} }) {
  // Reads two distinct config sources:
  // (1) /tryon/config — feature on/off + banner image URLs (admin → /admin/tryon)
  // (2) `sectionCfg` (passed by Home.jsx from the home_sections row) — the
  //     editable text content (eyebrow, heading, sub, primary + secondary CTAs)
  //     which the admin manages from the Home Editor's Edit drawer.
  //
  // Per-gender curation: sectionCfg.by_gender = { men, women, couple } — each
  // sub-object can override any/all fields of the base config. Fields missing
  // in the gender override fall back to the base sectionCfg (global default).
  // Selected via the sitewide gender pill in the header.
  const { gender } = useGender();
  const [tryonCfg, setTryonCfg] = React.useState({ enabled: true, home_banner_enabled: true, home_banner_user_photo: "", home_banner_tryon_photo: "" });
  React.useEffect(() => {
    let cancelled = false;
    api.get("/tryon/config").then(({ data }) => {
      if (cancelled) return;
      setTryonCfg(data || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const visible = !!tryonCfg.enabled && !!tryonCfg.home_banner_enabled;
  if (!visible) return null;
  // Pick per-gender overrides then merge onto base cfg. Keys with empty
  // strings / null are ignored so admin can leave a field blank in a
  // gender variant to inherit from the global row.
  const byGender = sectionCfg.by_gender || {};
  const gk = (gender || "men").toLowerCase();
  const genderCfg = byGender[gk] || {};
  const pick = (k, fallback) => {
    const v = genderCfg[k];
    if (v !== undefined && v !== null && v !== "") return v;
    return fallback;
  };
  // Image source priority: gender override → section config → legacy
  // /tryon/config values (set via /admin/tryon) → Unsplash placeholder.
  const userPhoto = pick("banner_user_photo", sectionCfg.banner_user_photo || tryonCfg.home_banner_user_photo || "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=600&q=80&auto=format&fit=crop");
  const tryonPhoto = pick("banner_tryon_photo", sectionCfg.banner_tryon_photo || tryonCfg.home_banner_tryon_photo || "https://images.unsplash.com/photo-1581655353564-df123a1eb820?w=600&q=80&auto=format&fit=crop");
  const eyebrow = pick("eyebrow", sectionCfg.eyebrow || "NEW · AI VIRTUAL TRY-ON");
  const heading = pick("heading", sectionCfg.heading || "See yourself in it, before you buy.");
  const sub = pick("sub", sectionCfg.sub || "Upload one photo. Our AI fits the tee on you — keeping your face, hair and pose intact. No guesswork. No regret returns.");
  const ctaLabel = pick("cta_label", sectionCfg.cta_label || "Try it on a product");
  const ctaUrl = pick("cta_url", sectionCfg.cta_url || "/shop");
  const secondaryLabel = pick("secondary_cta_label", sectionCfg.secondary_cta_label || "See an example →");
  const secondaryUrl = pick("secondary_cta_url", sectionCfg.secondary_cta_url || "/p/embroidered-polo-shirt");
  // Editable 3-step explainer + closing footnote — per-gender override wins
  // when the array is non-empty (letting admin blank out a gender to reuse
  // the global steps).
  const genderSteps = Array.isArray(genderCfg.steps) && genderCfg.steps.length ? genderCfg.steps : null;
  const steps = genderSteps || (Array.isArray(sectionCfg.steps) && sectionCfg.steps.length
    ? sectionCfg.steps
    : [
        { n: "1", label: "Pick any product you like" },
        { n: "2", label: "Tap “Try on with my photo”" },
        { n: "3", label: "See the AI-rendered preview in ~10 seconds" },
      ]);
  const footnote = pick("footnote", sectionCfg.footnote || "100% private · Your photo is only visible to you.");
  return (
    <section className="bg-white py-8 md:py-14" data-testid="home-tryon-promo">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#111111] via-[#1a1a1a] to-[#0f0f0f] text-white">
          {/* Decorative — orange spotlights */}
          <div aria-hidden="true" className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#FF5722]/25 blur-3xl pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-32 -right-16 w-80 h-80 rounded-full bg-[#FF5722]/15 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr,1fr] gap-8 lg:gap-12 p-6 md:p-10 lg:p-14 items-center">
            {/* LEFT — photo collage that visually explains the promise */}
            <div className="order-2 lg:order-1">
              {/* Image tiles ~30% larger than before + tight gap around the
                  sparkle icon (auto-sized center column instead of an equal
                  third) — drives more visual focus to the before/after pair. */}
              <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3 md:gap-4 max-w-[680px] mx-auto lg:mx-0">
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-zinc-800">
                  <img
                    src={userPhoto}
                    alt="Your photo"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <div className="text-[9px] uppercase tracking-wider font-black">Your photo</div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#FF5722] inline-flex items-center justify-center">
                    <Sparkles size={18} />
                  </div>
                </div>
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-zinc-800">
                  <img
                    src={tryonPhoto}
                    alt="AI Try-On result"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 inline-block bg-[#FF5722] text-white text-[8px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded">AI</div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <div className="text-[9px] uppercase tracking-wider font-black">Try-on</div>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-white/60 text-center mt-3">~10 seconds · No app download</div>
            </div>

            {/* RIGHT — value prop + 3-step explainer + CTA */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-1.5 bg-[#FF5722]/15 text-[#FF7043] text-[10px] uppercase tracking-[0.2em] font-black px-2.5 py-1 rounded">
                <Camera size={11} /> {eyebrow}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.05] mt-3" data-testid="home-tryon-heading">
                {heading}
              </h2>
              <p className="text-white/70 text-sm md:text-base mt-3 max-w-xl" data-testid="home-tryon-sub">
                {sub}
              </p>

              <ol className="mt-6 space-y-2.5 text-sm" data-testid="home-tryon-steps">
                {steps.map((step, idx) => {
                  const n = step?.n || String(idx + 1);
                  const label = step?.label || "";
                  if (!label) return null;
                  return (
                    <li key={`${n}-${idx}`} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/10 text-[#FF7043] text-xs font-black inline-flex items-center justify-center border border-white/15">{n}</span>
                      <span className="text-white/85">{label}</span>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={ctaUrl}
                  data-testid="home-tryon-cta"
                  className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-orange-600 text-white font-black text-xs uppercase tracking-wider h-11 px-6 rounded-lg transition-colors"
                >
                  {ctaLabel} <ArrowRight size={14} />
                </Link>
                {secondaryLabel && (
                  <Link
                    to={secondaryUrl}
                    data-testid="home-tryon-cta-secondary"
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-wider font-bold"
                  >
                    {secondaryLabel}
                  </Link>
                )}
              </div>
              <div className="text-[11px] text-white/40 mt-3" data-testid="home-tryon-footnote">{footnote}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


